// Transform builder config to Wagtail StreamField JSON
import { normalizeFilterParams } from './filterParams';
import { supportsStackValues } from './chartStacking';

const CHART_TYPE_MAP = {
    line: 'fenix_line_chart',
    area: 'fenix_stacked_area_chart',
    column: 'fenix_column_chart',
    bar: 'fenix_bar_chart',
    polar: 'fenix_polar_chart',
    treemap: 'fenix_tree_map_chart',
    timeline: 'fenix_timeline_chart',
    mixed: 'fenix_mixed_chart',
    heatmap: 'fenix_heat_map_chart',
};

const camelToSnake = (str) =>
    str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const wrapField = (field) => ({ field });

const wrapDataset = (dataset) => [
    {
        type: 'dataset',
        value: { dataset },
    },
];

// Serialize the editor's in-memory filter list ([{field, op, value}, ...])
// into the flat Wagtail filter_query.filters shape. `operator` is always
// emitted explicitly — even when its value is `'eq'` — so that round-trips
// converge old data to the canonical shape on first save.
const serializeFilters = (filters = []) => ({
    filters: normalizeFilterParams(filters).map((f) => ({
        field: f.field,
        operator: f.op || 'eq',
        value: String(f.value ?? ''),
    })),
});

const transformZone = (zone) => {
    if (!zone || !zone.axis || !zone.startValue) return null;
    return {
        axis: zone.axis,
        start_value: zone.startValue,
        end_value: zone.endValue || '',
    };
};

const transformAnnotations = (annotations = []) => {
    if (!annotations || annotations.length === 0) return [];
    // UI creates flat annotation items, wrap each in its own annotation block
    return annotations.map((item) => ({
        type: 'annotation',
        value: {
            show_only_on_countries: item.showOnlyOnCountries || [],
            annotation_items: [
                {
                    x_axis_coord: item.xAxisCoord || '',
                    x_axis_relative_alignment:
                        item.xAxisRelativeAlignment || '',
                    y_axis_coord: item.yAxisCoord || '',
                    y_axis_relative_alignment:
                        item.yAxisRelativeAlignment || '',
                    text: item.text || '',
                },
            ],
        },
    }));
};

const transformStandardSeries = (series, chartType) => {
    return series.map((s) => ({
        type: 'property_series',
        value: {
            data_source: wrapDataset(s.dataSource?.value || ''),
            filter_query: serializeFilters(s.filterParams),
            serie: {
                property: wrapField(s.serie?.property || ''),
                label: s.serie?.label || '',
                color: s.serie?.color || '#4FA0FF',
                negate_serie_values: s.serie?.negateSerieValues || false,
                x_axis_category_property: wrapField(
                    s.serie?.xAxisCategoryProperty || ''
                ),
                zone: transformZone(s.serie?.zone) || {
                    axis: '',
                    start_value: '',
                    end_value: '',
                },
            },
            annotations: transformAnnotations(s.annotations),
        },
    }));
};

const transformMixedSeries = (series) => {
    return series.map((s) => ({
        type: 'property_series',
        value: {
            data_source: wrapDataset(s.dataSource?.value || ''),
            filter_query: serializeFilters(s.filterParams),
            serie: {
                property: wrapField(s.serie?.property || ''),
                label: s.serie?.label || '',
                color: s.serie?.color || '#4FA0FF',
                negate_serie_values: s.serie?.negateSerieValues || false,
                x_axis_category_property: wrapField(
                    s.serie?.xAxisCategoryProperty || ''
                ),
                zone: transformZone(s.serie?.zone) || {
                    axis: '',
                    start_value: '',
                    end_value: '',
                },
            },
            series_chart_type: s.seriesChartType || 'line',
            annotations: transformAnnotations(s.annotations),
        },
    }));
};

const transformTimelineSeries = (series) => {
    return series.map((s) => ({
        type: 'property_series',
        value: {
            data_source: wrapDataset(s.dataSource?.value || ''),
            filter_query: serializeFilters(s.filterParams),
            serie: {
                date_property: wrapField(
                    s.serie?.dateProperty || s.serie?.property || ''
                ),
                title_property: wrapField(
                    s.serie?.titleProperty || s.serie?.label || ''
                ),
                description_property: wrapField(
                    s.serie?.descriptionProperty || ''
                ),
            },
        },
    }));
};

const transformHeatmapSeries = (series) => {
    return series.map((s) => ({
        type: 'property_series',
        value: {
            data_source: wrapDataset(s.dataSource?.value || ''),
            filter_query: serializeFilters(s.filterParams),
            serie: {
                property: wrapField(s.serie?.property || ''),
                x_axis_category_property: wrapField(
                    s.serie?.xAxisCategoryProperty || ''
                ),
                y_axis_category_property: wrapField(
                    s.serie?.yAxisCategoryProperty || ''
                ),
            },
        },
    }));
};

const transformTreemapSeries = (
    series,
    hierarchyLevels = [],
    colorMapping = []
) => {
    return series.map((s) => ({
        type: 'property_series',
        value: {
            data_source: wrapDataset(s.dataSource?.value || ''),
            filter_query: serializeFilters(s.filterParams),
            hierarchy_levels: hierarchyLevels.map((level) => ({
                ontology_property:
                    level.property || level.ontologyProperty || '',
                label_property: level.labelProperty || '',
                use_for_coloring: level.useForColoring || false,
            })),
            color_mapping: colorMapping.map((cm) => ({
                ontology_property: cm.ontologyProperty || cm.property || '',
                condition: cm.condition || 'equals',
                value: cm.value || '',
                case_sensitive: cm.caseSensitive || false,
                color: cm.color || '#4FA0FF',
            })),
            serie: {
                property: wrapField(s.serie?.property || ''),
                label: s.serie?.label || '',
            },
        },
    }));
};

const transformDynamicProperty = (dynamicProperty) => {
    if (!dynamicProperty || !dynamicProperty.property) return null;

    return {
        property: dynamicProperty.property,
        options_select_style: dynamicProperty.optionsSelectStyle || 'dropdown',
        populate_automatically: dynamicProperty.populateAutomatically || false,
        property_filter_options: (
            dynamicProperty.propertyFilterOptions || []
        ).map((opt) => ({
            value: opt.value,
            label: opt.label || opt.value,
        })),
    };
};

export const configToWagtail = (config) => {
    const chartType = config.chartType;
    if (!chartType || !CHART_TYPE_MAP[chartType]) {
        return { success: false, error: 'Unsupported or missing chart type' };
    }

    const wagtailType = CHART_TYPE_MAP[chartType];
    const isTimeline = chartType === 'timeline';
    const isTreemap = chartType === 'treemap';
    const isMixed = chartType === 'mixed';
    const isHeatmap = chartType === 'heatmap';

    const baseValue = {
        title: config.title || '',
        display_large_title: config.displayLargeTitle || false,
        show_title_in_toc: config.showTitleInToc || false,
        hide_chart_for_countries: config.hideChartForCountries || [],
        grid_span: config.gridSpan || '1',
    };

    // Include in overview (Fenix charts have this)
    if ('includeInOverview' in config) {
        baseValue.include_in_overview = config.includeInOverview || false;
    }

    let chartValue;

    if (isHeatmap) {
        chartValue = {
            ...baseValue,
            y_axis_label: config.yAxis?.label || '',
            y_axis_category_ordering: config.yAxis?.categoryOrdering || 'asc',
            y_axis_category_sorting_type:
                config.yAxis?.categorySortingType || 'string',
            // Note: Wagtail has a typo with 3 t's
            y_axis_label_formattting: config.yAxis?.labelFormatting || 'none',
            x_axis_label: config.xAxis?.label || '',
            x_axis_category_ordering: config.xAxis?.categoryOrdering || 'asc',
            x_axis_category_sorting_type:
                config.xAxis?.categorySortingType || 'string',
            // Note: Wagtail has a typo with 3 t's
            x_axis_label_formattting: config.xAxis?.labelFormatting || 'none',
            decimal_places: config.decimalPlaces ?? null,
            color_scheme: config.colorScheme || 'blue',
            series: transformHeatmapSeries(config.series || []),
        };
        // include_in_overview not on heatmap (1:1 with Foundry HeatMapChartBlock)
        delete chartValue.include_in_overview;
    } else if (isTimeline) {
        chartValue = {
            ...baseValue,
            space_points_evenly: config.spacePointsEvenly || false,
            date_ordering: config.dateOrdering || 'asc',
            // Note: Wagtail has a typo with 3 t's
            date_formattting: config.dateFormatting || 'none',
            color: config.timelineColor || 'auto',
            series: transformTimelineSeries(config.series || []),
        };
    } else if (isTreemap) {
        chartValue = {
            ...baseValue,
            layout_algorithm: config.layoutAlgorithm || 'sliceAndDice',
            border_width: config.borderWidth ?? 3,
            border_color: config.borderColor || '#fff',
            series: transformTreemapSeries(
                config.series || [],
                config.hierarchyLevels || [],
                config.colorMapping || []
            ),
        };
        // Add dynamic property if present
        const dynamicProp = transformDynamicProperty(config.dynamicProperty);
        if (dynamicProp) {
            chartValue.dynamic_property = dynamicProp;
        }
    } else {
        // Standard charts: line, area, column, bar, polar
        chartValue = {
            ...baseValue,
            aggregate_data_by_category: config.aggregateDataByCategory || false,
            y_axis_label: config.yAxis?.label || '',
            y_axis_min: config.yAxis?.min ?? null,
            y_axis_max: config.yAxis?.max ?? null,
            y_axis_tick_interval: config.yAxis?.tickInterval ?? null,
            decimal_places: config.decimalPlaces ?? null,
            x_axis_label: config.xAxis?.label || '',
            x_axis_category_ordering: config.xAxis?.categoryOrdering || 'asc',
            x_axis_category_sorting_type:
                config.xAxis?.categorySortingType || 'number',
            // Note: Wagtail has a typo with 3 t's
            x_axis_label_formattting: config.xAxis?.labelFormatting || 'none',
            series: isMixed
                ? transformMixedSeries(config.series || [])
                : transformStandardSeries(config.series || [], chartType),
        };

        // Chart-specific fields
        if (
            chartType === 'line' ||
            chartType === 'area' ||
            chartType === 'polar' ||
            chartType === 'mixed'
        ) {
            chartValue.enable_line_symbols = config.enableLineSymbols || false;
        }

        if (chartType === 'area') {
            chartValue.stacking_type = config.stackingType || 'normal';
        }

        if (supportsStackValues(chartType)) {
            chartValue.y_axis_stack_values = config.yAxis?.stackValues || false;
        }

        if (chartType === 'polar') {
            chartValue.polar_chart_value_type =
                config.polarChartValueType || 'line';
        }

        // Add dynamic property if present
        const dynamicProp = transformDynamicProperty(config.dynamicProperty);
        if (dynamicProp) {
            chartValue.dynamic_property = dynamicProp;
        }
    }

    return {
        success: true,
        json: {
            type: wagtailType,
            value: chartValue,
        },
    };
};

export default configToWagtail;
